import { PlusOutlined } from '@ant-design/icons';
import {
  ProFormDigit,
  ModalForm,
  PageContainer,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
  ProTable,
} from '@ant-design/pro-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Flex,
  Form,
  Popconfirm,
  Segmented,
  Space,
  Tag,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import FactViewSectionMetricConfig from '../components/FactViewSectionMetricConfig';
import MetricEffectivePeriodConfig from '../components/MetricEffectivePeriodConfig';
import ExpenseExclusionManager from '../components/ExpenseExclusionManager';
import MetricFormFields from '../components/MetricFormFields';
import MetricBatchEditForm from '../components/MetricBatchEditForm';
import { MetricExpressionFormItem } from '../components/MetricExpressionEditor';
import {
  allocationTypeValueEnum,
  attributionTypeValueEnum,
  getEffectiveMetricAllocationType,
  getEffectiveMetricPeriodType,
  getMetricAllocationPresentation,
  getMetricBatchUpdateValues,
  getMetricDefaultAllocationType,
  getMetricDefaultPeriodType,
  getMetricSourceDomainPresentation,
  isMetricBatchEditable,
  isMappingMetricCompatible,
  sourceDomainValueEnum,
  statisticalModelTypeValueEnum,
  periodTypeValueEnum,
} from '../components/metric-options';
import {
  createFactViewSection,
  createCompositeMetricExpression,
  createAtomicMetricForMapping,
  createOperationMetric,
  autoConfigureAtomicMetricMappings,
  autoConfigureCompositeMetricExpressions,
  batchUpdateOperationMetricConfigurations,
  queryFactViewMetrics,
  queryFactViewSections,
  queryAtomicMetricMappingsWithMeta,
  queryCompositeMetricExpressions,
  queryOperationMetrics,
  removeFactViewSection,
  removeOperationMetric,
  removeAtomicMetricMapping,
  saveFactViewSectionMetrics,
  saveAtomicMetricMapping,
  updateFactViewSection,
  updateCompositeMetricExpression,
  updateOperationMetric,
} from '../services/operations';
import { tokenizeMetricExpression } from '../utils/metric-expression';
import { getMetricLifecycleChangeValues } from '../utils/metric-lifecycle';

const METRIC_TYPE = {
  atomic: { text: '原子指标', status: 'Success' },
  composite: { text: '复合指标', status: 'Processing' },
};

const VALUE_TYPE = {
  amount: { text: '金额' },
  quantity: { text: '数量' },
  ratio: { text: '比率' },
};

const CALCULATION_MODEL = {
  atomic: '原子聚合',
  composite: '复合表达式',
};

const CALCULATION_MODE = {
  post_aggregate: { text: '公式后置' },
  pre_aggregate: { text: '公式前置' },
};

const MetricLifecycleModalForm = ({ children, onValuesChange, ...modalFormProps }) => {
  const [form] = Form.useForm();

  return (
    <ModalForm
      {...modalFormProps}
      form={form}
      omitNil={false}
      onValuesChange={(changedValues, values) => {
        const siblingValues = getMetricLifecycleChangeValues({
          changedValues,
          currentValues: values,
        });
        if (Object.keys(siblingValues).length > 0) {
          form.setFieldsValue(siblingValues);
        }
        onValuesChange?.(changedValues, values);
      }}
    >
      {children}
    </ModalForm>
  );
};

MetricLifecycleModalForm.propTypes = {
  children: PropTypes.node,
  onValuesChange: PropTypes.func,
};

const ENABLED = {
  true: { text: '启用', status: 'Success' },
  false: { text: '停用', status: 'Default' },
};
const MAPPING_STATUS = {
  mapped: { text: '已映射' },
  unmapped: { text: '未映射' },
};

const RULE_OPERATOR_PATTERN = /^[()+*/-]$/;
const FACT_VIEW_OPTIONS = [
  { label: '损益报表', value: 'fact_report' },
  { label: '经营分析', value: 'fact_chart' },
];
const FACT_VIEW_VALUE_ENUM = {
  fact_chart: { text: '经营分析' },
  fact_report: { text: '损益报表' },
};
const METRIC_TAB_KEYS = new Set(['metrics', 'effective-period', 'mappings', 'rules', 'fact-view']);
const MAPPING_DOMAIN_KEYS = new Set(['expense', 'income', 'plan', 'salary']);
const MAPPING_DOMAIN_OPTIONS = [
  { label: '经营计划', value: 'plan' },
  { label: '经营收入', value: 'income' },
  { label: '门店工资', value: 'salary' },
  { label: '经营费用', value: 'expense' },
];

const renderMetricAllocationType = (record) => {
  const presentation = getMetricAllocationPresentation(record);
  if (presentation.status === 'not_applicable') {
    return <Typography.Text type="secondary">不适用</Typography.Text>;
  }
  if (presentation.status === 'unmapped') {
    return <Tag color="warning">待映射</Tag>;
  }
  if (presentation.status === 'cross_domain') {
    return <Tag color="error">配置异常</Tag>;
  }
  return allocationTypeValueEnum[presentation.allocationType]?.text || '-';
};

const renderRuleExpression = (expression, metrics) => {
  const tokens = tokenizeMetricExpression(expression, metrics);
  if (tokens.length === 0) {
    return <Typography.Text type="secondary">未配置</Typography.Text>;
  }
  let tokenKeyPrefix = '';
  const keyedTokens = tokens.map((token) => {
    tokenKeyPrefix += `/${token.type}:${token.text}`;
    return { ...token, key: tokenKeyPrefix };
  });

  return (
    <Space wrap size={[4, 8]}>
      {keyedTokens.map(({ key, label, text, type }) => {
        if (type === 'operator' || RULE_OPERATOR_PATTERN.test(text)) {
          return (
            <Tag key={key} color="default">
              {text}
            </Tag>
          );
        }
        if (type === 'metric') {
          return (
            <Tag key={key} color="geekblue">
              {label || text}
            </Tag>
          );
        }
        return (
          <Typography.Text key={key} type="secondary">
            {text}
          </Typography.Text>
        );
      })}
    </Space>
  );
};

const formatMetricOptionLabel = (metric) => metric.name;

const renderMappingStatus = (record) => {
  if (record.mappingStatus === 'unmapped' || record.mapped === false) {
    return <Tag color="error">未映射</Tag>;
  }
  return <Tag color="success">已映射</Tag>;
};

const renderMetricMappingCell = (value, record) =>
  record.mappingStatus === 'unmapped' ? <Tag color="warning">请尽快配置</Tag> : value || '-';

const getMetricMappingActionText = (record) => {
  if (record.mappingStatus === 'unmapped') {
    return '配置映射';
  }
  return '编辑映射';
};

const getMappingSourcePayload = (record) => {
  if (record.sourceDomain === 'expense') {
    return {
      expenseTypeLevel1: record.expenseTypeLevel1,
      expenseTypeLevel2: record.expenseTypeLevel2,
      expenseTypeLevel3: record.expenseTypeLevel3,
    };
  }
  if (record.sourceDomain === 'income') {
    return {
      incomeTypeLevel1: record.incomeTypeLevel1,
      incomeTypeLevel2: record.incomeTypeLevel2,
      incomeTypeLevel3: record.incomeTypeLevel3,
      incomeValueType: record.incomeValueType,
    };
  }
  if (record.sourceDomain === 'salary') {
    return {
      salaryTypeLevel1: record.salaryTypeLevel1,
      salaryTypeLevel2: record.salaryTypeLevel2,
      salaryTypeLevel3: record.salaryTypeLevel3,
      salaryValueType: record.salaryValueType,
    };
  }
  return {
    planItemName: record.planItemName,
    planValueType: record.planValueType,
  };
};

const getMappingSourceColumns = (sourceDomain) => {
  if (sourceDomain === 'expense') {
    return [
      { title: '一级费用类型', dataIndex: 'expenseTypeLevel1' },
      { title: '二级费用类型', dataIndex: 'expenseTypeLevel2' },
      { title: '三级费用类型', dataIndex: 'expenseTypeLevel3' },
    ];
  }
  if (sourceDomain === 'income') {
    return [
      { title: '一级收入类型', dataIndex: 'incomeTypeLevel1' },
      { title: '二级收入类型', dataIndex: 'incomeTypeLevel2' },
      { title: '三级收入类型', dataIndex: 'incomeTypeLevel3' },
      { title: '数值类型', dataIndex: 'incomeValueType', valueEnum: VALUE_TYPE },
    ];
  }
  if (sourceDomain === 'salary') {
    return [
      { title: '一级工资类型', dataIndex: 'salaryTypeLevel1' },
      { title: '二级工资类型', dataIndex: 'salaryTypeLevel2' },
      { title: '三级工资类型', dataIndex: 'salaryTypeLevel3' },
      { title: '数值类型', dataIndex: 'salaryValueType', valueEnum: VALUE_TYPE },
    ];
  }
  return [
    { title: '计划项目', dataIndex: 'planItemName' },
    { title: '计划值类型', dataIndex: 'planValueType', valueEnum: VALUE_TYPE },
  ];
};

const MetricMappingFormFields = ({ metricOptions }) => (
  <>
    <ProFormSelect
      name="metricId"
      label="目标指标"
      options={metricOptions}
      rules={[{ required: true, message: '请选择目标指标' }]}
      fieldProps={{ optionFilterProp: 'label', showSearch: true }}
    />
  </>
);

MetricMappingFormFields.propTypes = {
  metricOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
};

const MetricRuleFormFields = ({ metric, metrics }) => (
  <>
    <MetricExpressionFormItem metric={metric} metrics={metrics} />
    <ProFormSwitch name="enabled" label="是否启用" />
  </>
);

MetricRuleFormFields.propTypes = {
  metric: PropTypes.shape({
    metricId: PropTypes.string,
    valueType: PropTypes.string,
  }).isRequired,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      metricType: PropTypes.string,
      name: PropTypes.string,
      valueType: PropTypes.string,
    }),
  ).isRequired,
};

const FactViewSectionFormFields = () => (
  <>
    <ProFormSelect
      name="viewKey"
      label="页面名称"
      options={FACT_VIEW_OPTIONS}
      rules={[{ required: true, message: '请选择页面' }]}
    />
    <ProFormText
      name="title"
      label="分组标题"
      rules={[{ required: true, message: '请输入分组标题' }]}
    />
    <ProFormDigit
      name="sortOrder"
      label="排序"
      fieldProps={{ precision: 0 }}
      rules={[{ required: true, message: '请输入排序值' }]}
    />
    <ProFormSwitch name="enabled" label="是否启用" />
  </>
);

const Metrics = () => {
  const metricActionRef = useRef(null);
  const mappingActionRef = useRef(null);
  const ruleActionRef = useRef(null);
  const factSectionActionRef = useRef(null);
  const factViewMetricDraftRef = useRef({});
  const [query, setQuery] = useQueryParams();
  const [mappingMeta, setMappingMeta] = useState({ unmappedMappingCount: 0 });
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const selectedMetricIds = selectedMetrics.map(({ id }) => id);
  const { organizationId } = useOrganizationPath();
  const { message } = App.useApp();
  const activeTab = METRIC_TAB_KEYS.has(query.tab) ? query.tab : 'metrics';
  const mappingDomain = MAPPING_DOMAIN_KEYS.has(query.mappingDomain)
    ? query.mappingDomain
    : 'expense';

  useEffect(() => {
    setSelectedMetrics([]);
  }, [organizationId]);

  const { mutateAsync: createMetric, isPending: creating } = useMutation({
    mutationFn: createOperationMetric,
    onSuccess: () => {
      message.success('复合指标已创建');
      metricActionRef.current?.reload?.();
      ruleActionRef.current?.reload?.();
      refetchRuleMetrics();
    },
    onError: () => {
      message.error('复合指标创建失败，请重试');
    },
  });

  const { mutateAsync: updateMetric, isPending: updating } = useMutation({
    mutationFn: updateOperationMetric,
    onSuccess: () => {
      message.success('指标已更新');
      metricActionRef.current?.reload?.();
    },
    onError: () => {
      message.error('指标更新失败，请重试');
    },
  });

  const { mutateAsync: batchUpdateMetricConfigurations, isPending: batchUpdating } = useMutation({
    mutationFn: batchUpdateOperationMetricConfigurations,
    onSuccess: (result, payload) => {
      const updatedCount =
        result?.data?.updatedCount ?? result?.updatedCount ?? payload?.data?.metricIds?.length ?? 0;
      message.success(`已更新 ${updatedCount} 个指标配置`);
      setSelectedMetrics([]);
      metricActionRef.current?.reload?.();
      mappingActionRef.current?.reload?.();
      refetchRuleMetrics();
    },
  });

  const { data: ruleMetricResult = { data: [] }, refetch: refetchRuleMetrics } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryOperationMetrics({ current: 1, organizationId, pageSize: 1000 }),
    queryKey: ['settings-rule-metrics', organizationId],
  });
  const { data: factViewMetricResult = { data: [] }, refetch: refetchFactViewMetrics } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryFactViewMetrics({ current: 1, organizationId, pageSize: 1000 }),
    queryKey: ['settings-fact-view-metrics', organizationId],
  });
  const { data: metricRuleResult = { data: [] }, refetch: refetchMetricRules } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryCompositeMetricExpressions({ current: 1, organizationId, pageSize: 1000 }),
    queryKey: ['settings-metric-rules', organizationId],
  });
  const ruleMetrics = ruleMetricResult.data || [];
  const factViewMetrics = factViewMetricResult.data || [];
  const metricRules = metricRuleResult.data || [];
  const hasCompositeMetric = ruleMetrics.some((metric) => metric.metricType === 'composite');
  const getMappingMetricOptions = (record) =>
    ruleMetrics
      .filter(
        (metric) =>
          metric.metricType === 'atomic' &&
          (metric.sourceDomain === mappingDomain ||
            (!metric.sourceDomain && Number(metric.mappingComboCount || 0) === 0)) &&
          isMappingMetricCompatible(mappingDomain, record.sourceValueType, metric),
      )
      .map((metric) => ({
        label: formatMetricOptionLabel(metric),
        value: metric.id,
      }));

  const { mutateAsync: removeMetric, isPending: removingMetric } = useMutation({
    mutationFn: removeOperationMetric,
    onSuccess: () => {
      message.success('指标已删除');
      metricActionRef.current?.reload?.();
      mappingActionRef.current?.reload?.();
      ruleActionRef.current?.reload?.();
      refetchRuleMetrics();
      refetchMetricRules();
      refetchFactViewMetrics();
    },
    onError: () => {
      message.error('指标删除失败，请重试');
    },
  });

  const reloadMetricMappings = () => {
    metricActionRef.current?.reload?.();
    mappingActionRef.current?.reload?.();
  };

  const { mutateAsync: createRule, isPending: creatingRule } = useMutation({
    mutationFn: createCompositeMetricExpression,
    onSuccess: () => {
      message.success('复合指标表达式已创建');
      ruleActionRef.current?.reload?.();
      refetchMetricRules();
    },
    onError: () => {
      message.error('复合指标表达式创建失败，请重试');
    },
  });

  const { mutateAsync: updateRule, isPending: updatingRule } = useMutation({
    mutationFn: updateCompositeMetricExpression,
    onSuccess: () => {
      message.success('复合指标表达式已更新');
      ruleActionRef.current?.reload?.();
      refetchMetricRules();
    },
    onError: () => {
      message.error('复合指标表达式更新失败，请重试');
    },
  });

  const { mutateAsync: saveFactViewSection, isPending: updatingFactViewSection } = useMutation({
    mutationFn: updateFactViewSection,
    onSuccess: () => {
      message.success('页面分组已更新');
      factSectionActionRef.current?.reload?.();
    },
    onError: () => {
      message.error('页面分组更新失败，请重试');
    },
  });

  const { mutateAsync: addFactViewSection, isPending: creatingFactViewSection } = useMutation({
    mutationFn: createFactViewSection,
    onSuccess: () => {
      message.success('页面分组已创建');
      factSectionActionRef.current?.reload?.();
    },
    onError: () => {
      message.error('页面分组创建失败，请重试');
    },
  });

  const { mutateAsync: removeSection, isPending: removingFactViewSection } = useMutation({
    mutationFn: removeFactViewSection,
    onSuccess: () => {
      message.success('页面分组已删除');
      factSectionActionRef.current?.reload?.();
      refetchFactViewMetrics();
    },
    onError: () => {
      message.error('页面分组删除失败，请重试');
    },
  });

  const { mutateAsync: saveFactViewMetrics, isPending: savingFactViewMetrics } = useMutation({
    mutationFn: saveFactViewSectionMetrics,
    onSuccess: () => {
      message.success('页面指标配置已保存');
      refetchFactViewMetrics();
    },
    onError: () => {
      message.error('页面指标配置保存失败，请重试');
    },
  });

  const { mutateAsync: saveMapping, isPending: savingMapping } = useMutation({
    mutationFn: saveAtomicMetricMapping,
    onSuccess: () => {
      message.success('指标映射已保存');
      reloadMetricMappings();
    },
    onError: () => {
      message.error('指标映射保存失败，请重试');
    },
  });

  const { mutateAsync: createAtomicMapping, isPending: creatingAtomicMapping } = useMutation({
    mutationFn: createAtomicMetricForMapping,
    onSuccess: () => {
      message.success('原子指标已创建并完成映射');
      reloadMetricMappings();
      refetchRuleMetrics();
    },
    onError: () => {
      message.error('原子指标创建失败，请重试');
    },
  });

  const { mutateAsync: removeMapping, isPending: removingMapping } = useMutation({
    mutationFn: removeAtomicMetricMapping,
    onSuccess: () => {
      message.success('指标映射已删除');
      reloadMetricMappings();
    },
    onError: () => {
      message.error('指标映射删除失败，请重试');
    },
  });

  const { mutateAsync: autoConfigureMapping, isPending: autoConfiguringMapping } = useMutation({
    mutationFn: autoConfigureAtomicMetricMappings,
    onError: () => {
      message.error('自动配置失败，请重试');
    },
  });

  const { mutateAsync: autoConfigureRules, isPending: autoConfiguringRules } = useMutation({
    mutationFn: autoConfigureCompositeMetricExpressions,
    onError: () => {
      message.error('自动匹配失败，请重试');
    },
  });

  const handleAutoConfigureMappings = async () => {
    const result = await autoConfigureMapping({
      data: {
        sourceDomain: mappingDomain,
        organizationId,
      },
    });
    const configuredCount = result?.data?.configuredCount ?? result?.configuredCount ?? 0;
    reloadMetricMappings();
    if (configuredCount === 0) {
      message.info('当前来源暂无需要自动配置的指标映射');
      return;
    }
    message.success(`已自动配置 ${configuredCount} 个指标映射`);
  };

  const handleAutoConfigureRules = async () => {
    const result = await autoConfigureRules({
      data: {
        organizationId,
      },
    });
    const configuredCount = result?.data?.configuredCount ?? result?.configuredCount ?? 0;
    metricActionRef.current?.reload?.();
    ruleActionRef.current?.reload?.();
    refetchRuleMetrics();
    refetchMetricRules();
    if (configuredCount === 0) {
      message.info('暂无需要自动匹配的复合指标表达式');
      return;
    }
    message.success(`已自动匹配 ${configuredCount} 个复合指标表达式`);
  };

  const submitRuleForm = async (values, submit) => {
    try {
      await submit({
        ...values,
      });
      return true;
    } catch {
      return false;
    }
  };

  const getSectionMetrics = (sectionId) =>
    factViewMetrics
      .filter((metric) => metric.sectionId === sectionId)
      .sort((prev, next) => prev.sortOrder - next.sortOrder);

  const metricColumns = [
    { title: '指标名称', dataIndex: 'name' },
    { title: '指标类型', dataIndex: 'metricType', valueEnum: METRIC_TYPE },
    {
      title: '领域类型',
      dataIndex: 'sourceDomain',
      valueEnum: sourceDomainValueEnum,
      search: false,
      render: (_, record) => getMetricSourceDomainPresentation(record),
    },
    {
      title: '计算模型',
      search: false,
      render: (_, record) => CALCULATION_MODEL[record.metricType] || '-',
    },
    { title: '值类型', dataIndex: 'valueType', valueEnum: VALUE_TYPE },
    {
      title: '生效策略',
      dataIndex: 'allocationType',
      valueEnum: allocationTypeValueEnum,
      render: (_, record) => renderMetricAllocationType(record),
    },
    {
      title: '归属策略',
      dataIndex: 'attributionType',
      valueEnum: attributionTypeValueEnum,
      render: (_, record) =>
        record.metricType === 'atomic'
          ? attributionTypeValueEnum[record.attributionType || 'direct']?.text
          : '-',
    },
    {
      title: '推测模型',
      dataIndex: 'statisticalModelType',
      valueEnum: statisticalModelTypeValueEnum,
      search: false,
    },
    {
      title: '指标周期',
      dataIndex: 'periodType',
      valueEnum: periodTypeValueEnum,
      search: false,
      render: (_, record) =>
        periodTypeValueEnum[getEffectiveMetricPeriodType(record)]?.text ||
        (record.metricType === 'atomic' && record.valueType !== 'ratio' ? '不规则' : '-'),
    },
    { title: '指标状态', dataIndex: 'enabled', valueEnum: ENABLED },
    {
      title: '来源映射数',
      dataIndex: 'mappingComboCount',
      valueType: 'digit',
      search: false,
    },
    { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
    { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false },
    {
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      width: 120,
      render: (_, record) => [
        <MetricLifecycleModalForm
          key={`edit:${record.updatedAt}`}
          title="修改指标"
          trigger={<Button type="link">修改指标</Button>}
          width={480}
          omitNil={false}
          initialValues={record}
          modalProps={{
            destroyOnHidden: true,
            okButtonProps: { loading: updating },
          }}
          onFinish={async (values) => {
            try {
              await updateMetric({
                data: {
                  id: record.id,
                  organizationId,
                  ...values,
                },
              });
              return true;
            } catch {
              return false;
            }
          }}
        >
          <MetricFormFields
            lockTypes
            mappingComboCount={record.mappingComboCount}
            sourceDomain={record.sourceDomain}
          />
        </MetricLifecycleModalForm>,
        <Popconfirm
          key="remove"
          title="删除指标"
          description="删除后该指标相关映射、计算规则和页面配置将一并删除。"
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true, loading: removingMetric }}
          onConfirm={() =>
            removeMetric({
              data: {
                id: record.id,
                organizationId,
              },
            })
          }
        >
          <Button danger type="link">
            删除指标
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  const mappingColumns = [
    {
      title: '指标名称',
      dataIndex: 'metricName',
      search: { order: 60 },
      render: (value, record) => renderMetricMappingCell(value, record),
    },
    {
      title: '映射状态',
      dataIndex: 'mappingStatus',
      valueEnum: MAPPING_STATUS,
      search: { order: 50 },
      render: (_, record) => renderMappingStatus(record),
    },
    ...getMappingSourceColumns(mappingDomain),
    {
      title: '生效策略',
      search: false,
      render: (_, record) => {
        const allocationType = getEffectiveMetricAllocationType(record);
        return allocationTypeValueEnum[allocationType]?.text || '-';
      },
    },
    {
      title: '指标状态',
      dataIndex: 'metricEnabled',
      valueEnum: ENABLED,
      search: false,
    },
    { title: '来源记录数', dataIndex: 'sourceEventCount', valueType: 'digit', search: false },
    { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false },
    {
      title: '操作',
      valueType: 'option',
      width: 140,
      fixed: 'right',
      render: (_, record) => {
        const configuringMapping = record.mappingStatus === 'unmapped';
        return [
          configuringMapping && (
            <MetricLifecycleModalForm
              key={`create-metric-${record.sourceKey}`}
              title="创建原子指标"
              trigger={<Button type="link">创建指标</Button>}
              width={480}
              initialValues={{
                allocationType:
                  record.sourceValueType === 'ratio' && mappingDomain !== 'salary'
                    ? null
                    : getMetricDefaultAllocationType(mappingDomain),
                attributionType: 'direct',
                enabled: true,
                metricType: 'atomic',
                name: record.sourceName,
                periodType: getMetricDefaultPeriodType(mappingDomain, record.sourceValueType),
                sourceDomain: mappingDomain,
                valueType: record.sourceValueType,
              }}
              modalProps={{
                destroyOnHidden: true,
                okButtonProps: { loading: creatingAtomicMapping },
              }}
              onFinish={async (values) => {
                try {
                  await createAtomicMapping({
                    data: {
                      organizationId,
                      sourceDomain: mappingDomain,
                      ...getMappingSourcePayload(record),
                      ...values,
                      metricType: 'atomic',
                      valueType: record.sourceValueType,
                    },
                  });
                  return true;
                } catch {
                  return false;
                }
              }}
            >
              <MetricFormFields
                fixedMetricType="atomic"
                lockTypes
                mappingComboCount={1}
                sourceDomain={mappingDomain}
              />
            </MetricLifecycleModalForm>
          ),
          <ModalForm
            key={`save-mapping-${record.id || record.sourceKey}`}
            title={configuringMapping ? '配置指标映射' : '修改指标映射'}
            trigger={<Button type="link">{getMetricMappingActionText(record)}</Button>}
            width={480}
            initialValues={{
              metricId: record.metricId,
            }}
            modalProps={{
              destroyOnHidden: true,
              okButtonProps: { loading: savingMapping },
            }}
            onFinish={async (values) => {
              try {
                await saveMapping({
                  data: {
                    organizationId,
                    sourceDomain: mappingDomain,
                    ...getMappingSourcePayload(record),
                    ...values,
                  },
                });
                return true;
              } catch {
                return false;
              }
            }}
          >
            <MetricMappingFormFields metricOptions={getMappingMetricOptions(record)} />
          </ModalForm>,
          !configuringMapping && (
            <Popconfirm
              key={`remove-mapping-${record.id}`}
              title="删除映射"
              description="删除后该来源类型将回到未映射状态。"
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true, loading: removingMapping }}
              onConfirm={() => removeMapping({ data: { id: record.id, organizationId } })}
            >
              <Button type="link" danger>
                删除映射
              </Button>
            </Popconfirm>
          ),
        ];
      },
    },
  ];

  const ruleColumns = [
    { title: '指标名称', dataIndex: 'metricName', width: 180 },
    { title: '值类型', dataIndex: 'valueType', valueEnum: VALUE_TYPE, width: 100 },
    {
      title: '复合指标表达式',
      dataIndex: 'expression',
      search: false,
      width: 420,
      render: (_, record) => renderRuleExpression(record.expression, ruleMetrics),
    },
    {
      title: '计算时机',
      dataIndex: 'calculationMode',
      valueEnum: CALCULATION_MODE,
      search: false,
      width: 120,
    },
    { title: '指标状态', dataIndex: 'enabled', valueEnum: ENABLED, width: 120 },
    { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false, width: 180 },
    {
      title: '操作',
      valueType: 'option',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        const configured = Boolean(record.id);
        return [
          <ModalForm
            key={`save-rule-${record.metricId}`}
            title={`${configured ? '修改' : '配置'}${record.metricName}计算表达式`}
            trigger={<Button type="link">{configured ? '修改表达式' : '配置表达式'}</Button>}
            width={560}
            initialValues={{
              calculationMode: record.calculationMode || 'post_aggregate',
              enabled: record.enabled ?? true,
              expression: record.expression,
            }}
            modalProps={{
              destroyOnHidden: true,
              okButtonProps: { loading: configured ? updatingRule : creatingRule },
            }}
            onFinish={(values) =>
              submitRuleForm(values, (payload) =>
                configured
                  ? updateRule({
                      data: {
                        id: record.id,
                        metricId: record.metricId,
                        organizationId,
                        ...payload,
                      },
                    })
                  : createRule({
                      data: { metricId: record.metricId, organizationId, ...payload },
                    }),
              )
            }
          >
            <MetricRuleFormFields metric={record} metrics={ruleMetrics} />
          </ModalForm>,
        ];
      },
    },
  ];

  const factSectionColumns = [
    { title: '页面名称', dataIndex: 'viewKey', valueEnum: FACT_VIEW_VALUE_ENUM },
    { title: '分组标题', dataIndex: 'title' },
    { title: '排序', dataIndex: 'sortOrder', search: false },
    { title: '状态', dataIndex: 'enabled', valueEnum: ENABLED, search: false },
    { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false },
    {
      title: '操作',
      valueType: 'option',
      width: 120,
      fixed: 'right',
      render: (_, record) => [
        <ModalForm
          key="edit-fact-section"
          title="修改页面分组"
          trigger={<Button type="link">修改分组</Button>}
          width={480}
          initialValues={record}
          modalProps={{
            destroyOnHidden: true,
            okButtonProps: { loading: updatingFactViewSection },
          }}
          onFinish={async (values) => {
            try {
              await saveFactViewSection({
                data: {
                  id: record.id,
                  organizationId,
                  ...values,
                },
              });
              return true;
            } catch {
              return false;
            }
          }}
        >
          <FactViewSectionFormFields />
        </ModalForm>,
        <ModalForm
          key={`config-section-metrics-${record.id}`}
          title="配置指标"
          trigger={<Button type="link">配置指标</Button>}
          width={760}
          initialValues={{
            includeSummary: getSectionMetrics(record.id).some(
              (metric) => metric.role === 'summary',
            ),
            itemMetricIds: getSectionMetrics(record.id)
              .filter((metric) => metric.role === 'item')
              .map((metric) => metric.metricId),
            metrics: getSectionMetrics(record.id).map((metric) => ({
              enabled: metric.enabled,
              metricId: metric.metricId,
              role: metric.role,
              sortOrder: metric.sortOrder,
            })),
            summaryMetricId: getSectionMetrics(record.id).find(
              (metric) => metric.role === 'summary',
            )?.metricId,
          }}
          modalProps={{
            destroyOnHidden: true,
            okButtonProps: { loading: savingFactViewMetrics },
          }}
          onFinish={async (values) => {
            try {
              await saveFactViewMetrics({
                data: {
                  metrics: factViewMetricDraftRef.current[record.id] || values.metrics || [],
                  organizationId,
                  sectionId: record.id,
                },
              });
              return true;
            } catch {
              return false;
            }
          }}
        >
          <FactViewSectionMetricConfig
            existingMetrics={getSectionMetrics(record.id)}
            metricRules={metricRules}
            metrics={ruleMetrics}
            onMetricsChange={(metrics) => {
              factViewMetricDraftRef.current[record.id] = metrics;
            }}
            renderExpression={(expression) => renderRuleExpression(expression, ruleMetrics)}
          />
        </ModalForm>,
        <Popconfirm
          key="remove-fact-section"
          title="删除分组"
          description="删除后该分组配置的指标将一并删除。"
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true, loading: removingFactViewSection }}
          onConfirm={() =>
            removeSection({
              data: {
                id: record.id,
                organizationId,
              },
            })
          }
        >
          <Button danger type="link">
            删除分组
          </Button>
        </Popconfirm>,
      ],
    },
  ];

  const tableProps = {
    rowKey: 'id',
    search: { labelWidth: 96 },
    scroll: { x: 'max-content' },
  };

  return (
    <PageContainer
      content="管理员维护经营指标口径、有效周期、来源映射和复合指标表达式。"
      tabActiveKey={activeTab}
      onTabChange={(key) => setQuery({ tab: key })}
      tabList={[
        { key: 'metrics', tab: '指标列表' },
        { key: 'effective-period', tab: '指标有效周期' },
        { key: 'mappings', tab: '原子指标映射' },
        { key: 'rules', tab: '复合指标表达式' },
        { key: 'fact-view', tab: '页面指标配置' },
      ]}
    >
      {activeTab === 'effective-period' && (
        <MetricEffectivePeriodConfig organizationId={organizationId} />
      )}
      {activeTab === 'metrics' && (
        <ProTable
          {...tableProps}
          actionRef={metricActionRef}
          headerTitle="指标列表"
          request={(params) => queryOperationMetrics({ ...params, organizationId })}
          columns={metricColumns}
          rowSelection={{
            getCheckboxProps: (record) => {
              const editable = isMetricBatchEditable(record);
              const ineligibleReason = '仅已映射的经营费用、门店工资金额原子指标支持批量编辑';
              return {
                'aria-label': editable
                  ? `选择指标 ${record.name}`
                  : `指标 ${record.name} 不可批量编辑：${ineligibleReason}`,
                disabled: !editable,
                title: editable ? undefined : ineligibleReason,
              };
            },
            onChange: (_, selectedRows) => setSelectedMetrics(selectedRows),
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedMetricIds,
          }}
          toolBarRender={() => [
            <MetricBatchEditForm
              key="batch-edit-metric-config"
              loading={batchUpdating}
              metricCount={selectedMetricIds.length}
              hasSalaryMetrics={selectedMetrics.some(
                ({ sourceDomain }) => sourceDomain === 'salary',
              )}
              onFinish={async (values) => {
                try {
                  await batchUpdateMetricConfigurations({
                    data: {
                      ...getMetricBatchUpdateValues(values),
                      metricIds: selectedMetricIds,
                      organizationId,
                    },
                  });
                  return true;
                } catch {
                  return false;
                }
              }}
            />,
          ]}
        />
      )}
      {activeTab === 'mappings' && (
        <Flex vertical gap="middle">
          <Segmented
            value={mappingDomain}
            options={MAPPING_DOMAIN_OPTIONS}
            onChange={(value) => {
              setMappingMeta({ unmappedMappingCount: 0 });
              setQuery({ mappingDomain: value });
            }}
          />
          {mappingMeta.unmappedMappingCount > 0 && (
            <Alert
              showIcon
              type="warning"
              description={`当前来源有 ${mappingMeta.unmappedMappingCount} 个类型尚未配置指标映射。`}
              action={
                <Button
                  size="small"
                  type="primary"
                  loading={autoConfiguringMapping}
                  onClick={handleAutoConfigureMappings}
                >
                  自动配置
                </Button>
              }
            />
          )}
          <ProTable
            {...tableProps}
            actionRef={mappingActionRef}
            headerTitle="原子指标映射"
            key={mappingDomain}
            rowKey={(record) => record.sourceKey}
            request={async (params) => {
              const result = await queryAtomicMetricMappingsWithMeta({
                ...params,
                organizationId,
                sourceDomain: mappingDomain,
              });
              setMappingMeta(result.meta || { unmappedMappingCount: 0 });
              return result;
            }}
            columns={mappingColumns}
            toolBarRender={() =>
              mappingDomain === 'expense'
                ? [<ExpenseExclusionManager key={organizationId} organizationId={organizationId} />]
                : []
            }
          />
        </Flex>
      )}
      {activeTab === 'rules' && (
        <Flex vertical gap="middle">
          <Alert
            showIcon
            type={hasCompositeMetric ? 'info' : 'warning'}
            description={
              hasCompositeMetric
                ? '可根据当前原子指标自动匹配复合指标表达式。'
                : '当前尚未配置复合指标表达式，请自动匹配后再维护。'
            }
            action={
              <Button
                size="small"
                type="primary"
                loading={autoConfiguringRules}
                onClick={handleAutoConfigureRules}
              >
                自动匹配
              </Button>
            }
          />
          <ProTable
            {...tableProps}
            actionRef={ruleActionRef}
            headerTitle="复合指标表达式"
            request={async (params) => {
              return queryCompositeMetricExpressions({ ...params, organizationId });
            }}
            columns={ruleColumns}
            rowKey="metricId"
            scroll={{ x: 1280 }}
            toolBarRender={() => [
              <ModalForm
                key="create-composite-metric"
                title="创建复合指标"
                trigger={
                  <Button type="primary" icon={<PlusOutlined />}>
                    创建复合指标
                  </Button>
                }
                width={480}
                initialValues={{
                  enabled: true,
                  metricType: 'composite',
                  valueType: 'amount',
                }}
                modalProps={{
                  destroyOnHidden: true,
                  okButtonProps: { loading: creating },
                }}
                onFinish={async (values) => {
                  try {
                    await createMetric({
                      data: {
                        organizationId,
                        ...values,
                        metricType: 'composite',
                      },
                    });
                    return true;
                  } catch {
                    return false;
                  }
                }}
              >
                <MetricFormFields fixedMetricType="composite" />
              </ModalForm>,
            ]}
          />
        </Flex>
      )}
      {activeTab === 'fact-view' && (
        <Flex vertical gap="middle">
          <ProTable
            {...tableProps}
            actionRef={factSectionActionRef}
            headerTitle={false}
            request={(params) => queryFactViewSections({ ...params, organizationId })}
            columns={factSectionColumns}
            pagination={{ pageSize: 10 }}
            toolBarRender={() => [
              <ModalForm
                key="create-fact-section"
                title="创建页面分组"
                trigger={
                  <Button type="primary" icon={<PlusOutlined />}>
                    创建分组
                  </Button>
                }
                width={480}
                initialValues={{ enabled: true, sortOrder: 0, viewKey: 'fact_report' }}
                modalProps={{
                  destroyOnHidden: true,
                  okButtonProps: { loading: creatingFactViewSection },
                }}
                onFinish={async (values) => {
                  try {
                    await addFactViewSection({
                      data: {
                        organizationId,
                        ...values,
                      },
                    });
                    return true;
                  } catch {
                    return false;
                  }
                }}
              >
                <FactViewSectionFormFields />
              </ModalForm>,
            ]}
          />
        </Flex>
      )}
    </PageContainer>
  );
};

export default Metrics;
